import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import PageShell from '../components/PageShell';
import { cpFetch } from '../lib/cpClient';
import { GitBranch, Bot, FileText, Shield, Rocket, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────

type Delta = {
  delta_id?: string;
  timestamp: string;
  change_type: string;
  entity_type: string;
  entity_id: string;
  summary: string;
  affected_apps: string[];
  breaking: boolean;
  migration_action: string | null;
  metadata: Record<string, unknown>;
};

type DeltasResponse = {
  deltas: Delta[];
  total: number;
  limit: number;
  skip: number;
};

// ── Config ───────────────────────────────────────────────────────────────────

const CHANGE_TYPE_LABELS: Record<string, string> = {
  agent_registered:  'Agent Registered',
  agent_updated:     'Agent Updated',
  template_created:  'Template Created',
  template_updated:  'Template Updated',
  yoda_rule_added:   'Yoda Rule Added',
  platform_deployed: 'Platform Deployed',
  sandbox_created:   'Sandbox Created',
  sandbox_deleted:   'Sandbox Deleted',
};

const ENTITY_TYPE_LABELS: Record<string, string> = {
  agent:     'Agent',
  template:  'Template',
  yoda_rule: 'Yoda Rule',
  deploy:    'Deploy',
  sandbox:   'Sandbox',
};

type BadgeVariant = 'blue' | 'green' | 'orange' | 'purple' | 'red' | 'yellow';

const CHANGE_TYPE_VARIANTS: Record<string, BadgeVariant> = {
  agent_registered:  'green',
  agent_updated:     'green',
  template_created:  'orange',
  template_updated:  'orange',
  yoda_rule_added:   'purple',
  platform_deployed: 'blue',
  sandbox_created:   'yellow',
  sandbox_deleted:   'red',
};

const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  blue:   'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  green:  'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  red:    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
};

function ChangeTypeBadge({ changeType, breaking }: { changeType: string; breaking: boolean }) {
  const variant: BadgeVariant = breaking ? 'red' : (CHANGE_TYPE_VARIANTS[changeType] ?? 'blue');
  const label = CHANGE_TYPE_LABELS[changeType] ?? changeType;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${VARIANT_CLASSES[variant]}`}>
      {breaking && <AlertTriangle size={11} />}
      {label}
    </span>
  );
}

function EntityIcon({ entityType }: { entityType: string }) {
  if (entityType === 'agent')     return <Bot size={14} className="text-green-600" />;
  if (entityType === 'template')  return <FileText size={14} className="text-orange-600" />;
  if (entityType === 'yoda_rule') return <Shield size={14} className="text-purple-600" />;
  if (entityType === 'deploy')    return <Rocket size={14} className="text-blue-600" />;
  return <GitBranch size={14} className="text-slate-500" />;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30)  return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ── DeltaCard ────────────────────────────────────────────────────────────────

function DeltaCard({ delta }: { delta: Delta }) {
  const [expanded, setExpanded] = useState(false);
  const hasMetadata = delta.metadata && Object.keys(delta.metadata).length > 0;

  return (
    <div className={`border rounded-lg p-4 bg-white dark:bg-slate-800 ${delta.breaking ? 'border-red-400' : 'border-slate-200 dark:border-slate-700'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <div className="mt-0.5 shrink-0">
            <EntityIcon entityType={delta.entity_type} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <ChangeTypeBadge changeType={delta.change_type} breaking={delta.breaking} />
              <span className="text-xs text-slate-500 font-mono truncate max-w-xs">{delta.entity_id}</span>
            </div>
            <p className="mt-1 text-sm text-slate-800 dark:text-slate-200">{delta.summary}</p>
            {delta.migration_action && (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 rounded px-2 py-1">
                Action: {delta.migration_action}
              </p>
            )}
            {delta.affected_apps.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {delta.affected_apps.map(appId => (
                  <span key={appId} className="inline-block px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs rounded font-mono">
                    {appId}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <span className="text-xs text-slate-400" title={delta.timestamp}>{relativeTime(delta.timestamp)}</span>
          {hasMetadata && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="ml-2 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label="Toggle metadata"
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
        </div>
      </div>
      {expanded && hasMetadata && (
        <pre className="mt-3 text-xs bg-slate-50 dark:bg-slate-900 rounded p-3 overflow-auto text-slate-600 dark:text-slate-300">
          {JSON.stringify(delta.metadata, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function DeltaChangelogPage() {
  const [changeType, setChangeType] = useState('');
  const [entityType, setEntityType] = useState('');
  const [fromDate, setFromDate]     = useState('');
  const [toDate, setToDate]         = useState('');
  const [skip, setSkip]             = useState(0);
  const limit = 50;

  const { data, isLoading, error, refetch } = useQuery<DeltasResponse>({
    queryKey: ['delta-changelog', changeType, entityType, fromDate, toDate, skip],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (changeType) params.set('change_type', changeType);
      if (entityType) params.set('entity_type', entityType);
      if (fromDate)   params.set('from', new Date(fromDate).toISOString());
      if (toDate)     params.set('to',   new Date(toDate).toISOString());
      params.set('limit', String(limit));
      params.set('skip',  String(skip));
      return await cpFetch(`/api/deltas?${params}`);
    },
    refetchInterval: 60_000,
  });

  const deltas = data?.deltas ?? [];
  const total  = data?.total  ?? 0;

  return (
    <PageShell title="Delta Changelog" subtitle="Platform changes — agents, templates, Yoda rules, deploys">
      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500 font-medium">Change type</label>
          <select
            value={changeType}
            onChange={e => { setChangeType(e.target.value); setSkip(0); }}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded px-2 py-1.5 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 min-w-[180px]"
          >
            <option value="">All types</option>
            {Object.entries(CHANGE_TYPE_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500 font-medium">Entity type</label>
          <select
            value={entityType}
            onChange={e => { setEntityType(e.target.value); setSkip(0); }}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded px-2 py-1.5 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 min-w-[140px]"
          >
            <option value="">All entities</option>
            {Object.entries(ENTITY_TYPE_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500 font-medium">From</label>
          <input
            type="datetime-local"
            value={fromDate}
            onChange={e => { setFromDate(e.target.value); setSkip(0); }}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded px-2 py-1.5 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-500 font-medium">To</label>
          <input
            type="datetime-local"
            value={toDate}
            onChange={e => { setToDate(e.target.value); setSkip(0); }}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded px-2 py-1.5 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200"
          />
        </div>
        <button
          onClick={() => refetch()}
          className="px-3 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Results header */}
      {!isLoading && (
        <p className="text-xs text-slate-500 mb-3">
          {total > 0 ? `${total} change${total !== 1 ? 's' : ''} found` : 'No changes found'}
          {total > limit && ` · showing ${skip + 1}–${Math.min(skip + limit, total)}`}
        </p>
      )}

      {/* Content */}
      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
          <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
          Loading…
        </div>
      )}

      {error && (
        <div className="text-red-600 text-sm py-4">
          Failed to load deltas: {(error as Error).message}
        </div>
      )}

      {!isLoading && !error && deltas.length === 0 && (
        <div className="py-16 text-center text-slate-400">
          <GitBranch size={32} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">No platform changes recorded yet.</p>
          <p className="text-xs mt-1 text-slate-300">Deltas appear here as agents are registered, templates are created, and deploys run.</p>
        </div>
      )}

      <div className="space-y-3">
        {deltas.map((delta, i) => (
          <DeltaCard key={delta.delta_id ?? `delta-${i}`} delta={delta} />
        ))}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setSkip(Math.max(0, skip - limit))}
            disabled={skip === 0}
            className="px-3 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            Previous
          </button>
          <span className="text-xs text-slate-500">{skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
          <button
            onClick={() => setSkip(skip + limit)}
            disabled={skip + limit >= total}
            className="px-3 py-1.5 text-sm rounded border border-slate-300 dark:border-slate-600 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </PageShell>
  );
}
